/**
 * Validation primitives shared by every form and every server action.
 *
 * The same schema runs in the browser (for instant feedback) and on the server
 * (as the actual gate). The browser copy is a convenience; the server copy is
 * the one that matters, and no action trusts input that has not passed it.
 */
import { z } from 'zod';
import { isValidBdMobile, normalisePhone } from '@/lib/format';

/** A Prisma cuid. Rejects anything that is not plausibly one of our ids. */
export const idSchema = z
  .string()
  .trim()
  .min(1, 'Required')
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, 'Invalid identifier');

export const optionalId = idSchema.optional().or(z.literal('')).transform((v) => (v ? v : undefined));

/**
 * A monetary amount.
 *
 * Accepts what a person types (`1,200`, `৳1200`, `1200.50`) and normalises to a
 * plain decimal string. Never becomes a JS number.
 */
export function moneySchema(options: { min?: number; max?: number; label?: string } = {}) {
  const { min = 0, max = 99_999_999_999.99, label = 'Amount' } = options;
  return z
    .union([z.string(), z.number()])
    .transform((value) => String(value).trim().replace(/[,\s৳]/g, ''))
    .refine((value) => value !== '', { message: `${label} is required.` })
    .refine((value) => /^-?\d*\.?\d+$/.test(value), { message: `${label} must be a number.` })
    .refine((value) => Number(value) >= min, { message: `${label} cannot be less than ${min}.` })
    .refine((value) => Number(value) <= max, { message: `${label} is too large.` })
    .refine((value) => (value.split('.')[1]?.length ?? 0) <= 2, {
      message: `${label} can have at most 2 decimal places.`,
    });
}

/** An optional amount — empty means zero. */
export function optionalMoneySchema(options: { min?: number; max?: number; label?: string } = {}) {
  return z
    .union([z.string(), z.number(), z.undefined(), z.null()])
    .transform((value) => (value === undefined || value === null || String(value).trim() === '' ? '0' : value))
    .pipe(moneySchema(options));
}

/** A whole-unit quantity. Stock is counted in units, never fractions. */
export function quantitySchema(options: { min?: number; max?: number; label?: string } = {}) {
  const { min = 1, max = 1_000_000, label = 'Quantity' } = options;
  return z
    .union([z.string(), z.number()])
    .transform((value) => Number(String(value).trim()))
    .refine((value) => Number.isFinite(value), { message: `${label} must be a number.` })
    .refine((value) => Number.isInteger(value), { message: `${label} must be a whole number.` })
    .refine((value) => value >= min, { message: `${label} must be at least ${min}.` })
    .refine((value) => value <= max, { message: `${label} is too large.` });
}

/**
 * A quantity that may be left out, which reads as none.
 *
 * `quantitySchema` starts at 1 because you cannot sell nothing. A target is the
 * other case: leaving the units box empty is how somebody says "measure this
 * agent on money only", and that has to parse to zero rather than fail.
 */
export function optionalQuantitySchema(options: { max?: number; label?: string } = {}) {
  return z
    .union([z.string(), z.number(), z.undefined(), z.null()])
    .transform((value) => (value === undefined || value === null || String(value).trim() === '' ? 0 : value))
    .pipe(quantitySchema({ ...options, min: 0 }));
}

/** `YYYY-MM-DD`, the wire format for every business date. */
export const dateKeySchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date picker to choose a date.')
  .refine((value) => {
    const [y, m, d] = value.split('-').map(Number) as [number, number, number];
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() + 1 === m && date.getUTCDate() === d;
  }, 'That date does not exist.');

/** `YYYY-MM`, used by monthly targets and salary periods. */
export const monthKeySchema = z
  .string()
  .trim()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Choose a month.');

/** A Bangladesh mobile number, stored as `01XXXXXXXXX`. */
export const phoneSchema = z
  .string()
  .trim()
  .min(1, 'Mobile number is required.')
  .transform(normalisePhone)
  .refine(isValidBdMobile, 'Enter a valid Bangladesh mobile number, for example 01712345678.');

export const optionalPhoneSchema = z
  .string()
  .trim()
  .optional()
  .transform((v) => (v ? normalisePhone(v) : undefined))
  .refine((v) => v === undefined || isValidBdMobile(v), 'Enter a valid Bangladesh mobile number.');

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Enter a valid email address.')
  .max(190);

export const optionalEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(190)
  .optional()
  .transform((v) => (v ? v : undefined))
  .refine((v) => v === undefined || z.string().email().safeParse(v).success, 'Enter a valid email address.');

/** Free text that ends up in a notes / description field. */
export function textSchema(max: number, label = 'This field') {
  return z.string().trim().max(max, `${label} must be ${max} characters or fewer.`);
}

export function requiredTextSchema(max: number, label = 'This field') {
  return z.string().trim().min(1, `${label} is required.`).max(max, `${label} must be ${max} characters or fewer.`);
}

/** An IMEI: 15 digits. */
export const imeiSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s|-/g, ''))
  .refine((v) => /^\d{15}$/.test(v), 'An IMEI must be exactly 15 digits.');

export const optionalImeiSchema = z
  .string()
  .trim()
  .optional()
  .transform((v) => {
    const cleaned = v?.replace(/\s|-/g, '');
    return cleaned ? cleaned : undefined;
  })
  .refine((v) => v === undefined || /^\d{15}$/.test(v), 'An IMEI must be exactly 15 digits.');

/**
 * The client-generated transaction id that makes a write idempotent.
 * Required on anything that can be submitted twice — by a double tap or by an
 * offline queue replaying after reconnection.
 */
export const clientTxnIdSchema = z
  .string()
  .trim()
  .uuid('Invalid transaction id.')
  .optional();

export const paymentMethodSchema = z.enum(['CASH', 'BKASH', 'NAGAD', 'CARD', 'BANK', 'CREDIT', 'OTHER'], {
  message: 'Please select a payment method.',
});

export const settlementMethodSchema = z.enum(['CASH', 'BKASH', 'NAGAD', 'CARD', 'BANK', 'OTHER'], {
  message: 'Please select a payment method.',
});

/** Standard list query: pagination, sorting, search. */
export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  search: z.string().trim().max(120).optional(),
  sort: z.string().trim().max(60).optional(),
  direction: z.enum(['asc', 'desc']).default('desc'),
});

export type ListQuery = z.infer<typeof listQuerySchema>;

export const rangeQuerySchema = z.object({
  preset: z
    .enum(['today', 'yesterday', 'this_week', 'this_month', 'last_month', 'last_7_days', 'last_30_days', 'custom'])
    .default('today'),
  from: dateKeySchema.optional(),
  to: dateKeySchema.optional(),
  branchId: optionalId,
});

export type RangeQuery = z.infer<typeof rangeQuerySchema>;

/**
 * Turn a Zod failure into the field-error map the forms expect.
 * Nested paths are joined with dots so that `items.0.quantity` lines up with
 * the react-hook-form field name.
 */
export function fieldErrorsFrom(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const path = issue.path.join('.') || '_form';
    if (!out[path]) out[path] = issue.message;
  }
  return out;
}
